import { Poll } from 'schema/poll';
import { SendError } from '../machine';

type NonEmptyArray<T> = [T, ...T[]];
export type ParsedPreinitQuery = Array<[questionAnchor: string, values: NonEmptyArray<string>]>;

export type PreinitStateValue =
  | { literalValueOther: string }
  | { literalValue: string }
  | { answerId: string }
  | { answerIds: string[] };
export type PreinitState = Array<[questionId: string, value: PreinitStateValue]>;

declare global {
  interface String {
    split(separator: string | RegExp, limit?: number): [string, ...string[]];
  }
}

export function parsePreinitQuery(query: URLSearchParams, sendError: SendError): ParsedPreinitQuery {
  const map: ParsedPreinitQuery = [];
  for (const queryValue of query.getAll('preinit-answer-state[]')) {
    try {
      const [key, rawValue] = queryValue.split(':');
      if (!key || !rawValue) throw new Error('Malformed preinit-answer-state[] value: ' + queryValue);
      const value: [string, ...string[]] = rawValue?.startsWith('[')
        ? rawValue.replace(/^\[/, '').replace(/\]$/, '').split(',')
        : [rawValue];

      const existing = map.find(([k]) => k === key)?.[1];
      if (existing) {
        existing.push(...value);
      } else {
        map.push([key, value]);
      }
    } catch {
      sendError(new Error('Unknown error while parsing preinit query'), {
        additionalInfo: { query: query.toString() },
      });
    }
  }
  return map;
}

const findEntityByAnchorOrId = <T extends { anchor?: string; id: string }>(
  entities: T[],
  anchorOrId: string,
): T | null => {
  const foundByAnchor = entities.find(e => e.anchor === anchorOrId);
  if (foundByAnchor) return foundByAnchor;

  const foundById = entities.find(e => e.id === anchorOrId);
  return foundById ?? null;
};

export function parsedQueryToPreinitState(
  poll: Poll,
  parsedQuery: ParsedPreinitQuery,
  sendError: (v: Error) => void,
): PreinitState {
  const preinitState: PreinitState = [];

  for (const [key, values] of parsedQuery) {
    const question = findEntityByAnchorOrId(poll.questions, key);
    if (!question) continue;

    let result: PreinitStateValue | undefined;

    const checkSingleAnswer = () => {
      if (values.length > 1)
        sendError(new Error(`More than one preinit answer for a single answer question (${question.data.type})`));
    };

    const value = values[0];
    switch (question.data.type) {
      case 'POLL_TEXTAREA': {
        checkSingleAnswer();
        const answer = findEntityByAnchorOrId(question.data.answers, value);
        if (!answer) {
          result = { literalValueOther: value };
        } else {
          result = { answerId: answer.id };
        }
        break;
      }
      case 'POLL_RATING_DIGITS':
      case 'POLL_RATING_SLIDER': {
        checkSingleAnswer();
        const parsed = Number(value);
        if (isNaN(parsed) || parsed > question.data.maximumValue || parsed < question.data.minimumValue) {
          sendError(new Error(`Invalid rating digits/slider (${question.id}) value: ${parsed}`));
          break;
        }
        result = { literalValue: value };
        break;
      }
      case 'POLL_RATING_LIKE': {
        checkSingleAnswer();
        if (value !== '0' && value !== '1') {
          sendError(new Error('Invalid rating like value ' + value));
          break;
        }
        result = { literalValue: value };
        break;
      }
      case 'POLL_RATING_STARS':
      case 'POLL_RATING_SMILIES': {
        checkSingleAnswer();
        const parsed = Number(value);
        if (isNaN(parsed) || parsed > question.data.maximumValue || parsed < 1) {
          sendError(new Error(`Invalid rating stars/smilies (${question.id}) value: ${parsed}`));
          break;
        }
        result = { literalValue: value };
        break;
      }
      case 'POLL_MULTIPLE_FROM_PICTURE':
      case 'POLL_MULTIPLE_SELECT':
      case 'POLL_MULTIPLE': {
        const a = question.data.answers;
        const answers = values.map(v => findEntityByAnchorOrId(a, v)).filter((v): v is NonNullable<typeof v> => !!v);
        if (answers.length !== values.length) {
          sendError(new Error(`Nonexistent preinit answer anchors: ${values.toString()} (question: ${question.id})`));
        } else {
          result = { answerIds: answers.map(v => v.id) };
        }
        break;
      }
      case 'POLL_SINGLE':
      case 'POLL_SINGLE_FROM_PICTURE':
      case 'POLL_SELECT': {
        checkSingleAnswer();
        const answer = findEntityByAnchorOrId(question.data.answers, value);
        if (!answer) {
          sendError(new Error(`Nonexistent preinit answer anchor: ${value} (question: ${question.id})`));
        } else {
          result = { answerId: answer.id };
        }
        break;
      }

      default: {
        sendError(new Error(`Preinit answer for unsupported ${question.data.type}`));
        break;
      }
    }

    if (result) {
      preinitState.push([question.id, result]);
    }
  }

  return preinitState;
}
